import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { service } from '@ember/service'
import { htmlSafe, isHTMLSafe, type SafeString } from '@ember/template'
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { t, type IntlService } from 'ember-intl'
import { and, eq } from 'ember-truth-helpers'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import ChartInfoTooltip from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type { TOC } from '@ember/component/template-only'
import IsSubco from 'teacher-ui-client/helpers/is-subco'
import type Student from 'teacher-ui-client/models/student'
import {
  InlineSegmentedBar,
  SegmentedAxis,
  type AxisRangeItem,
  type RangeItem,
} from 'teacher-ui-client/components/giraffe/primitives/inline-segmented-bar'
import { differenceWith, range as _range } from 'lodash'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import type RouterService from '@ember/routing/router-service'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'

function getColor(
  product: string,
  precinct: string,
  studentLessonData?: {
    lesson: number
    assignment: boolean
  },
) {
  if (!studentLessonData) return 'bg-transparent'

  if (studentLessonData && studentLessonData.assignment) {
    return 'bg-purply-pink-300'
  }

  switch (`${product}.${precinct}`) {
    case 'maths.lessons':
      return 'bg-forest-green-300'
    case 're.spelling':
    case 'rex.spelling':
      return 'bg-grapey-green-300'
    case 're.reading':
    case 'rex.my_lessons':
      return 'bg-oceany-blue-300'
    default:
      return 'bg-oceany-blue-300'
  }
}

export type CourseProgressLessonData = {
  student_id: number
  lessons_completed: number
  max_lesson: number
  lessons: { lesson: number; assignment: boolean }[]
}

export type CourseProgressSpellingData = {
  student_id: number
  lessons_completed: number
  average_score: number
  lessons: { lesson: number; assignment: boolean }[]
}

type CourseProgressData = CourseProgressLessonData | CourseProgressSpellingData

interface HistoryDataViewModel {
  firstName: string
  gradePosition: number
  lastName: string
  lessonsCompleted?: number
  averageScore: Nullable<number>
  maxLesson: Nullable<number>
  studentId: string
  teacherName: string
  series: RangeItem[]
}

interface Args {
  studentRouteName: string
  precinct: 'lessons' | 'reading' | 'spelling' | 'my_lessons'
  product: 'maths' | 're' | 'rex'
  themeKey: ThemeKeys
  tooltip: string
  uiScope: UIScopeName
  students: Student[]
  courseProgressHistory: CourseProgressData[]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

const lessonsToGradePosition: Record<number, number | undefined> = {
  1: 1,
  51: 2,
  101: 3,
}

function gradeForLesson(lessonNumber?: number): number | undefined {
  if (!lessonNumber) return
  return lessonsToGradePosition[lessonNumber]
}

function buildTooltip(intl: IntlService, lessonsCompleted?: number, lessonNumber?: number) {
  return `${intl.t('reporting.tooltips.courseProgressHistory.chartItem.lessonTooltip', {
    lessonCount: lessonsCompleted,
    lessonNumber,
  })}`
}

export class CourseProgressHistory extends Component<Signature> {
  @tracked sortingPriorityQueue = []

  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  @service router!: RouterService

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get amendedTooltip(): string | SafeString {
    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.courseProgressHistory,
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(this.args.tooltip)
      const newTooltip = this.args.tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return this.args.tooltip
  }

  get historyData() {
    return this.args.courseProgressHistory ?? []
  }
  get lessonNumbers() {
    return this.historyData.reduce<number[]>((lessons, studentData) => {
      const studentLessons = studentData.lessons.map((lesson) => lesson.lesson)
      return lessons.concat(studentLessons)
    }, [])
  }

  get range() {
    return _range(Math.min(...this.lessonNumbers) ?? 1, (Math.max(...this.lessonNumbers) ?? 1) + 1)
  }

  get data(): HistoryDataViewModel[] {
    const students = this.args.students

    return students.filter(hasId).map((student) => {
      const studentData = this.historyData.find((data) => String(data.student_id) === student.id)

      const maxLesson = studentData && 'max_lesson' in studentData ? studentData.max_lesson : null
      const averageScore = studentData && 'average_score' in studentData ? studentData.average_score : null

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName:
          this.args.product === 'maths' ? student.mathsTeacherNames.join(', ') : student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        lessonsCompleted: studentData?.lessons_completed,
        maxLesson,
        averageScore,
        studentId: student.id,
        series: this.range.map<RangeItem>((lessonNumber) => {
          const lessonData = studentData?.lessons.find((lesson) => lesson.lesson === lessonNumber)

          const bgColor = getColor(this.args.product, this.args.precinct, lessonData)

          const tooltip =
            bgColor === 'bg-transparent'
              ? undefined
              : buildTooltip(this.intl, studentData?.lessons_completed, lessonNumber)

          return {
            bgColor,
            showBorder: Boolean(gradeForLesson(lessonNumber) && lessonNumber !== this.range[0]),
            tooltip,
          }
        }),
      }
    })
  }

  get axisRange(): AxisRangeItem[] {
    const min = this.range[0]
    const max = this.range[this.range.length - 1]

    return this.range.map((lessonNumber) => {
      const matching = gradeForLesson(lessonNumber)

      const gradeLabel = matching ? this.gradeSets.findByPosition(matching)?.fullName : undefined

      const shouldShowGradeLabel = Boolean(gradeLabel)

      /*
       * Show the label
       *  - if the lesson number is the first or last lesson
       *  - if the lesson matches a gradeLabel
       */
      const showLabel = shouldShowGradeLabel || lessonNumber === min || lessonNumber === max

      return {
        label: showLabel ? String(lessonNumber) : undefined,
        groupLabel: gradeLabel,
        isLast: lessonNumber === max,
      }
    })
  }

  get isSchoolScope(): boolean {
    return this.args.uiScope === 'school'
  }

  get isSpelling(): boolean {
    return this.args.precinct === 'spelling'
  }

  get showCharts() {
    return this.args.uiScope !== 'school'
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo(this.args.studentRouteName, studentId)
  }

  <template>
    <div
      data-test-giraffe-chart-course-progress-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative mb-6 rounded-sm border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip @tooltipContent={{this.amendedTooltip}} />
      {{#let this.processor.transformedData as |transformedData|}}
        <table class="w-full overflow-hidden rounded-sm text-sm">
          <thead
            class="{{if (eq @themeKey 'blue') 'bg-oceany-blue-300'}}
              {{if (eq @themeKey 'light-green') 'bg-grapey-green-300'}}
              {{if (eq @themeKey 'green') 'bg-forest-green-300'}}
              {{if (eq @themeKey 'default') 'bg-dusty-black-300'}}
              "
          >
            <tr class="divide-x divide-white">
              <TH class="w-36" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <span>{{t "reporting.dashboardTables.firstName" htmlSafe=true}}</span>
                <SortColumnIcon
                  class="ml-2"
                  @sortingPriorityQueue={{this.sortingPriorityQueue}}
                  @columnName="firstName"
                />
              </TH>
              <TH class="w-36" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                <SortColumnIcon
                  class="ml-2"
                  @sortingPriorityQueue={{this.sortingPriorityQueue}}
                  @columnName="lastName"
                />
              </TH>
              {{#if (IsSubco)}}
                <TH data-test-teacher-name {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}>
                  {{t "reporting.dashboardTables.teacher"}}
                  <SortColumnIcon
                    class="ml-2"
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                    @columnName="teacherName"
                  />
                </TH>
                <TH
                  data-test-grade-position
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  {{t "grade.label"}}
                  <SortColumnIcon
                    class="ml-2"
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                    @columnName="gradePosition"
                  />
                </TH>
              {{/if}}
              <TH {{on "click" (fn this.processor.tableActions.sortByNumber "lessonsCompleted")}}>
                {{t "reporting.charts.courseProgress.lessonsCompleted" htmlSafe=true}}
                <SortColumnIcon
                  class="ml-2"
                  @sortingPriorityQueue={{this.sortingPriorityQueue}}
                  @columnName="lessonsCompleted"
                />
              </TH>
              {{#if this.isSpelling}}
                <TH
                  data-test-course-progress-history-average-score-column
                  {{on "click" (fn this.processor.tableActions.sortByNumber "averageScore")}}
                >

                  {{t "reporting.charts.courseProgress.averageScore"}}
                  <SortColumnIcon
                    class="ml-2"
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                    @columnName="averageScore"
                  />
                </TH>
              {{else}}
                <TH
                  data-test-course-progress-history-lesson-completed-column
                  {{on "click" (fn this.processor.tableActions.sortByNumber "maxLesson")}}
                >
                  {{t "reporting.charts.courseProgress.highestLessonCompleted" htmlSafe=true}}
                  <SortColumnIcon
                    class="ml-2"
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                    @columnName="maxLesson"
                  />
                </TH>
              {{/if}}
              {{#if (and this.showCharts @students)}}
                <th
                  class="text-dusty-black-500 relative border-none
                    {{if (IsSubco) 'w-1/3' 'w-1/2'}}
                    bg-white print:hidden"
                >
                  <h3 class="px-2">{{t "reporting.chartTitles.courseProgressHistory"}}</h3>
                  <Legend @product={{@product}} @precinct={{@precinct}} />
                  <SegmentedAxis @axisRange={{this.axisRange}} />
                </th>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 print:border-dusty-black-100 cursor-pointer divide-y-2 print:border-t">
            {{#each transformedData as |row index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-course-progress-history-row={{index}}
                {{on "click" (fn this.transitionToStudentRoute row.studentId)}}
              >
                <TD>
                  {{row.firstName}}
                </TD>
                <TD class="text-left">
                  {{row.lastName}}
                </TD>
                {{#if (IsSubco)}}
                  <TD data-test-teacher-name class="text-left">
                    {{row.teacherName}}
                  </TD>
                  <TD data-test-grade-position class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <TD data-test-lesson-completed class="text-center">
                  {{formatValue row.lessonsCompleted}}
                </TD>
                {{#if this.isSpelling}}
                  <TD data-test-average-score class="text-center">
                    {{formatValue row.averageScore append="%"}}
                  </TD>
                {{else}}
                  <TD data-test-max-lesson class="text-center">
                    {{formatValue row.maxLesson}}
                  </TD>
                {{/if}}

                {{#if (and this.showCharts @students)}}
                  <td class="border-dusty-black-50 h-20 border-l print:hidden">
                    <InlineSegmentedBar class="h-full" @series={{row.series}} />
                  </td>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      {{/let}}
    </div>
  </template>
}

interface CellSignature {
  Element: HTMLTableCellElement
  Blocks: {
    default: []
  }
}

const TH: TOC<CellSignature> = <template>
  <th class="rounded-tl-md px-4 py-3 text-left font-normal text-wrap" role="button" ...attributes>
    <div class="flex items-center justify-between text-white print:text-black">
      {{yield}}
    </div>
  </th>
</template>

const TD: TOC<CellSignature> = <template>
  <td class="h-20 px-2 py-0" ...attributes>
    {{yield}}
  </td>
</template>

interface LegendSignature {
  Element: HTMLDivElement
  Args: {
    product: 'maths' | 're' | 'rex'
    precinct: 'lessons' | 'reading' | 'spelling' | 'my_lessons'
  }
}

class Legend extends Component<LegendSignature> {
  @service intl!: IntlService

  get legendScheme(): { color: string; left: string; right: string } {
    switch (`${this.args.product}.${this.args.precinct}`) {
      case 'maths.lessons':
        return {
          color: 'bg-forest-green-300',
          left: this.intl.t('reporting.maths.courseProgressHistory.lessonTypeName.lessons'),
          right: this.intl.t('reporting.assignments.label'),
        }
      case 're.reading':
        return {
          color: 'bg-oceany-blue-300',
          left: this.intl.t('reporting.re.courseProgressHistory.lessonTypeName.reading'),
          right: this.intl.t('reporting.assignments.label'),
        }
      case 're.spelling':
        return {
          color: 'bg-grapey-green-300',
          left: this.intl.t('reporting.re.courseProgressHistory.lessonTypeName.spelling'),
          right: this.intl.t('reporting.assignments.label'),
        }
      case 'rex.my_lessons':
        return {
          color: 'bg-oceany-blue-300',
          left: this.intl.t('reporting.rex.courseProgressHistory.lessonTypeName.my_lessons'),
          right: this.intl.t('reporting.assignments.label'),
        }
      case 'rex.spelling':
        return {
          color: 'bg-grapey-green-300',
          left: this.intl.t('reporting.rex.courseProgressHistory.lessonTypeName.spelling'),
          right: this.intl.t('reporting.assignments.label'),
        }
      default:
        return { color: '', left: '', right: '' }
    }
  }

  <template>
    <div class="mt-2 flex items-center px-2">
      <span data-test-course-progress-history-legend-left><span
          data-test-legend-color
          class="{{this.legendScheme.color}} mr-2 inline-block h-3 w-3 rounded-full"
        ></span>{{this.legendScheme.left}}</span>
      <span data-test-course-progress-history-legend-right class="ml-2"><span
          data-test-legend-color
          class="bg-purply-pink-300 mr-2 inline-block h-3 w-3 rounded-full"
        ></span>
        {{this.legendScheme.right}}</span>
    </div>
  </template>
}
